'use client';

import { useState, useEffect, useRef, useCallback, useId, memo, Suspense } from 'react';
import { Container, TextInput, SimpleGrid, Card, Image, Text, Box, Group, NativeSelect, Badge, Loader, Center, Alert, Skeleton, Paper, Title, Stack, Button, ActionIcon, Popover } from '@mantine/core';
import Link from 'next/link';
import { SetLabels } from '@/components/SetLabels';
import { CatalogFilters } from '@/components/CatalogFilters';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { IconAlertCircle, IconSearch, IconX, IconPackage, IconZoomQuestion, IconChecklist, IconCheck, IconArrowRight, IconSparkles, IconUsers, IconRefresh , IconHammer } from '@tabler/icons-react';
import api, { fetchCommunityProgress } from '@/lib/api';
import { notifyError } from '@/lib/notify';
import { friendlyMessage } from '@/lib/apiError';
import { getThumbUrl } from '@/lib/imageUrl';
import { VerificationBadge } from '@/components/VerificationBadge';
import { FavoriteHeart } from '@/components/FavoriteHeart';
import { BrickProgress } from '@/components/BrickProgress';
import { SortPartTile } from '@/components/sort/SortPartTile';
import { isSortModeNew } from '@/lib/sortModeLaunch';
import { isMultiBuildNew, MULTI_BUILD_PROMO_ENABLED } from '@/lib/multiBuildLaunch';
import { trackEvent } from '@/lib/telemetry';
import { useHomeSearch } from '@/context/HomeSearchContext';
import { useUser } from '@/context/UserContext';
import { fetchAllProgress, deleteProgress } from '@/lib/account';

function resultCountBucket(count) {
  if (!count) return '0';
  if (count === 1) return '1';
  if (count <= 5) return '2-5';
  if (count <= 20) return '6-20';
  if (count <= 50) return '21-50';
  return '51+';
}

// Ambient mini-voortgangsbalk in de "Check Mode"-feature-card: loopt traag
// vol in een loop zodat de kaart zichzelf demonstreert. Eigen component zodat
// de interval-ticks niet de hele homepage re-renderen.
function AmbientBrickProgress() {
  const [pct, setPct] = useState(70);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    setPct(0);
    const id = setInterval(() => {
      setPct(p => (p >= 100 ? 0 : Math.min(100, p + 100 / 12)));
    }, 700);
    return () => clearInterval(id);
  }, []);
  return (
    <BrickProgress
      value={pct} size="sm"
      aria-label="Sort Mode demo"
      style={{ maxWidth: 150, margin: '2px auto 6px' }}
    />
  );
}

// Image component with skeleton loading
const SetImage = memo(function SetImage({ src, alt, priority = false }) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  // SSR'd images can finish loading before hydration attaches onLoad;
  // without this check they stay stuck at opacity 0 behind the skeleton
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setLoaded(true);
    }
  }, []);

  return (
    <Box style={{ position: 'relative', width: '100%', height: '100%' }}>
      {!loaded && (
        <Skeleton
          style={{
            position: 'absolute',
            top: '10%',
            left: '10%',
            width: '80%',
            height: '80%',
            borderRadius: '8px'
          }}
          animate={true}
        />
      )}
      <Image
        ref={imgRef}
        src={src}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        fit="contain"
        h="100%"
        w="100%"
        style={{
          objectFit: 'contain',
          maxHeight: '100%',
          maxWidth: '100%',
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.3s ease',
          mixBlendMode: 'multiply',
        }}
        fallbackSrc="https://placehold.co/400x400?text=No+Image"
        onLoad={() => setLoaded(true)}
      />
    </Box>
  );
});

// Memoized card: the grid grows unbounded via infinite scroll, so parent state
// changes (scroll flags, demo ticks, search typing) must not repaint every card
const SetCard = memo(function SetCard({ set, priority, onYearClick, onOpen }) {
  const bagCount = set.bag_count || 0;
  const totalPieces = set.total_pieces || set.totalPieces || 0;
  const imageUrl = set.image_url ? getThumbUrl(set.image_url, 400) : 'https://placehold.co/400x200?text=No+Image';
  return (
    <Card
      component="article"
      padding={0}
      radius="lg"
      className="set-card"
      style={{ position: 'relative' }}
    >
      <Link
        href={`/set/${set.id}`}
        onClick={onOpen}
        className="set-card-link-overlay"
        aria-label={`Open ${set.name}, set ${set.id}`}
      />
      <Card.Section
        p="md"
        className="home-card-section"
        style={{
          aspectRatio: '1 / 1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #fff 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <VerificationBadge
          verified={set.verified}
          source={set.source}
          showUnverified={true}
          iconOnly={true}
          size="md"
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            zIndex: 2,
          }}
        />
        <FavoriteHeart
          setId={set.id}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 3,
          }}
        />
        <Box className="set-card-image">
          <SetImage src={imageUrl} alt={set.name} priority={priority} />
        </Box>
      </Card.Section>
      {(set.theme || set.year || set.labels?.length > 0 || (set.build_count || 0) >= 2) && (
        <Group gap={6} px="md" pt={8} pb={8}>
          {(set.build_count || 0) >= 2 && !(set.theme || '').toLowerCase().includes('-in-1') && (
            <Badge
              variant="light" size="sm" radius="sm"
              style={{
                backgroundColor: '#fff5f5', color: '#e03131', fontWeight: 700,
                border: '1px solid #ffc9c9', textTransform: 'none',
              }}
            >
              {set.build_count}-in-1
            </Badge>
          )}
          <SetLabels labels={set.labels} shop={set.catalog_shop} compact group="regular" />
          {set.theme && (
            <Badge
              variant="light" size="sm" radius="sm"
              style={{
                backgroundColor: '#f8f9fa', color: '#66707a', fontWeight: 500,
                border: '1px solid #e9ecef', textTransform: 'none',
              }}
            >
              {set.theme}
            </Badge>
          )}
          {set.year && (
            <Badge
              component="button"
              type="button"
              className="theme-badge-clickable"
              variant="light" size="sm" radius="sm"
              aria-label={`Show sets from ${set.year}`}
              onClick={() => onYearClick(set.year)}
              style={{
                position: 'relative', zIndex: 3,
                backgroundColor: '#f8f9fa', color: '#66707a', fontWeight: 500,
                border: '1px solid #e9ecef', cursor: 'pointer', textTransform: 'none',
              }}
            >
              {set.year}
            </Badge>
          )}
          <SetLabels labels={set.labels} shop={set.catalog_shop} compact group="special" />
        </Group>
      )}
      <Box p="md" className="home-card-info" style={{ borderTop: '1px solid #f1f3f4' }}>
        <Group justify="space-between" mb={6} gap="xs">
          <Group gap={6} align="center">
            <Text size="xs" fw={600} c="dimmed">
              {bagCount} {set.all_virtual ? (bagCount === 1 ? 'group' : 'groups') : (bagCount === 1 ? 'bag' : 'bags')}
            </Text>
            <Text size="xs" c="dimmed">•</Text>
            <Text size="xs" c="dimmed" fw={500}>
              {totalPieces} pcs
            </Text>
          </Group>
          <Text size="xs" c="dimmed" fw={500}>#{set.id}</Text>
        </Group>
        <Text component="h3" m={0} fw={600} size="sm" lineClamp={2} style={{ color: '#212529', lineHeight: 1.4 }}>
          {set.name}
        </Text>
      </Box>
    </Card>
  );
});

function HomePageContent({ initialData, initialQuery }) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const homeSearch = useHomeSearch();
  const { account } = useUser();
  const [isSticky, setIsSticky] = useState(false);
  const homeSearchInstanceId = useId().replaceAll(':', '');
  const homeSearchListboxId = `home-search-${homeSearchInstanceId}-suggestions`;
  const [mainSearchFocused, setMainSearchFocused] = useState(false);
  const [mainSearchDismissed, setMainSearchDismissed] = useState(false);
  const [mainSearchActive, setMainSearchActive] = useState(-1);
  const mainSearchInputRef = useRef(null);

  // Read filter values from URL search params
  const selectedTheme = searchParams.get('theme') || null;
  const sortBy = searchParams.get('sort') || 'popular';
  const searchQuery = searchParams.get('search') || '';
  const multibuildFilter = searchParams.get('multibuild') === '1';
  const selectedLabels = searchParams.get('labels') || '';
  const [catalog, setCatalog] = useState(initialData?.catalog || null);
  const catalogShop = initialQuery?.shop || catalog?.defaultLocale || 'da-DK';
  useEffect(() => {
    if (!catalog) api.fetchCatalogDefinitions().then(setCatalog).catch(console.error);
  }, [catalog]);

  // Helper to update URL params. Uses native history.replaceState (synced with
  // useSearchParams since Next 14.2) instead of router.replace: the homepage is
  // a dynamic route now, and router.replace would refetch the server payload on
  // every debounced search keystroke.
  const updateParams = useCallback((updates) => {
    const next = new URLSearchParams(searchParams.toString());
    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === undefined || value === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    }
    // Remove defaults to keep URL clean
    if (next.get('sort') === 'popular') next.delete('sort');
    const qs = next.toString();
    window.history.replaceState(null, '', qs ? `${pathname}?${qs}` : pathname);
  }, [searchParams, pathname]);

  const setSelectedTheme = useCallback((value) => {
    updateParams({ theme: value });
  }, [updateParams]);

  const setSortBy = useCallback((value) => {
    updateParams({ sort: value });
  }, [updateParams]);

  const handleYearClick = useCallback((year) => {
    router.push(`/?search=${year}`);
  }, [router]);

  // The query value lives in the shared HomeSearch context so the big search
  // bar and the compact header search are the same controlled value. Typing is
  // instant; the URL is synced with a short debounce (a router.replace per
  // keystroke drops characters).
  const searchInput = homeSearch?.value ?? '';
  const searchDebounceRef = useRef(null);
  const searchSessionActiveRef = useRef(false);
  const trackedSearchResultsRef = useRef(null);
  // Start as null so the first effect run treats the initial URL value as an
  // external change and seeds the shared context (handles /?search=foo loads).
  const lastPushedSearch = useRef(null);

  // A query can also arrive through a shared header search, year badge, or a
  // bookmarked URL. Treat that as the same single search session, without ever
  // sending the query value itself.
  useEffect(() => {
    if (searchQuery.trim()) {
      if (!searchSessionActiveRef.current) {
        searchSessionActiveRef.current = true;
        trackEvent('Home Search Started');
      }
    } else if (!searchInput.trim()) {
      searchSessionActiveRef.current = false;
      trackedSearchResultsRef.current = null;
    }
  }, [searchQuery, searchInput]);

  // Committer: pushes the value to the URL (debounced). Registered with the
  // context so both inputs route through it.
  const commitSearch = useCallback((value) => {
    if (value.trim()) {
      if (!searchSessionActiveRef.current) {
        searchSessionActiveRef.current = true;
        trackEvent('Home Search Started');
      }
    } else {
      searchSessionActiveRef.current = false;
      trackedSearchResultsRef.current = null;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      lastPushedSearch.current = value;
      updateParams({ search: value });
    }, 250);
  }, [updateParams]);

  const setSearchQuery = useCallback((value) => {
    homeSearch?.setQuery(value);
  }, [homeSearch]);

  // Keep the shared value + committer registered; also sync back from the URL
  // when it changes outside the inputs (back button, badge clicks).
  useEffect(() => {
    const fromUrl = searchQuery !== lastPushedSearch.current;
    if (fromUrl) lastPushedSearch.current = searchQuery;
    homeSearch?.registerHomeSearch(fromUrl ? searchQuery : searchInput, commitSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, searchInput, commitSearch]);

  // On unmount (leaving the homepage), let the header search go back to normal.
  useEffect(() => {
    return () => homeSearch?.unregisterHomeSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ITEMS_PER_PAGE = 50;

  // API state - seeded with the server-fetched payload so the grid is in the
  // initial HTML and no client fetch is needed for the first page
  const initialSetsData = initialData?.sets || null;
  const [legoSets, setLegoSets] = useState(initialSetsData?.sets ?? []);
  const [themes, setThemes] = useState(() => (initialData?.themes ? initialData.themes.map(t => t.theme) : []));
  const [loading, setLoading] = useState(!initialSetsData);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state
  const [hasMore, setHasMore] = useState(initialSetsData?.pagination?.hasMore ?? true);
  const [offset, setOffset] = useState(initialSetsData ? ITEMS_PER_PAGE : 0);
  const [totalCount, setTotalCount] = useState(initialSetsData?.pagination?.total ?? null);

  // Ref for infinite scroll observer
  const loadMoreRef = useRef(null);
  const loadMoreControllerRef = useRef(null);
  const activeFilterKeyRef = useRef('');
  const searchBarRef = useRef(null);
  const stickyBoxRef = useRef(null);
  const gridRef = useRef(null);

  const trackSearchResults = useCallback((query, count) => {
    if (!query.trim()) return;
    // The query itself is deliberately only used in-memory for deduplication;
    // it is never sent to analytics.
    const dedupKey = `${query}\u0000${count}`;
    if (trackedSearchResultsRef.current === dedupKey) return;
    trackedSearchResultsRef.current = dedupKey;
    trackEvent(count > 0 ? 'Home Search Results' : 'Home Search Zero Results', {
      result_count: resultCountBucket(count),
    });
  }, []);

  const trackSearchResultOpen = useCallback(() => {
    // A card can be opened before the 250ms URL debounce finishes. Cancel the
    // pending homepage navigation so it cannot pull the visitor back from the
    // set page to `/?search=...` after the link has already been followed.
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    trackEvent('Home Search Result Opened');
  }, []);

  // Fetch top themes from API (skipped when the server already provided them)
  useEffect(() => {
    if (initialData?.themes) return;
    const loadThemes = async () => {
      try {
        const data = await api.fetchTopThemes();
        setThemes(data.map(t => t.theme));
      } catch (err) {
        console.error('Failed to load themes:', err);
        // Fallback to empty array if themes fail to load
        setThemes([]);
      }
    };

    loadThemes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The server already fetched the sets for the query it rendered - skip the
  // very first client fetch as long as the URL query still matches it
  const serverQueryRef = useRef(initialSetsData ? initialQuery : null);

  // Fetch initial sets when theme or search changes (reset pagination).
  // Search is already debounced upstream (commitSearch, 250ms); abort any
  // in-flight request so a slow response can't overwrite a newer one.
  useEffect(() => {
    const serverQuery = serverQueryRef.current;
    if (serverQuery) {
      serverQueryRef.current = null;
      if ((serverQuery.theme || null) === selectedTheme &&
          (serverQuery.search || '') === searchQuery &&
          (serverQuery.sort || 'popular') === sortBy &&
          !!serverQuery.multibuild === multibuildFilter &&
          (serverQuery.labels || '') === selectedLabels &&
          (serverQuery.shop || catalog?.defaultLocale || 'da-DK') === catalogShop) {
        trackSearchResults(searchQuery, initialSetsData?.pagination?.total ?? initialSetsData?.sets?.length ?? 0);
        return;
      }
    }
    const controller = new AbortController();

    const loadSets = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await api.fetchSets(selectedTheme, searchQuery || null, ITEMS_PER_PAGE, 0, sortBy, controller.signal, multibuildFilter, {labels:selectedLabels, shop:catalogShop});
        setLegoSets(data.sets);
        setHasMore(data.pagination.hasMore);
        setTotalCount(data.pagination.total);
        setOffset(ITEMS_PER_PAGE);
        trackSearchResults(searchQuery, data.pagination.total ?? data.sets.length);
        setLoading(false);
      } catch (err) {
        if (err.name === 'AbortError') return;
        setError(friendlyMessage(err));
        console.error(err);
        setLoading(false);
      }
    };

    loadSets();

    return () => controller.abort();
  }, [selectedTheme, searchQuery, sortBy, multibuildFilter, selectedLabels, catalogShop, catalog?.defaultLocale, initialSetsData, trackSearchResults]);

  // Load more sets for infinite scroll
  const loadMoreSets = async () => {
    if (loadingMore || !hasMore) return;

    const requestFilterKey = JSON.stringify({ selectedTheme, searchQuery, sortBy, multibuildFilter, selectedLabels, catalogShop });
    const controller = new AbortController();
    loadMoreControllerRef.current?.abort();
    loadMoreControllerRef.current = controller;
    try {
      setLoadingMore(true);
      const data = await api.fetchSets(selectedTheme, searchQuery || null, ITEMS_PER_PAGE, offset, sortBy, controller.signal, multibuildFilter, {labels:selectedLabels, shop:catalogShop});
      if (controller.signal.aborted || activeFilterKeyRef.current !== requestFilterKey) return;
      setLegoSets(prev => [...prev, ...data.sets]);
      setHasMore(data.pagination.hasMore);
      setOffset(prev => prev + ITEMS_PER_PAGE);
    } catch (err) {
      if (err.name === 'AbortError' || controller.signal.aborted) return;
      console.error('Failed to load more sets:', err);
      notifyError(err, { title: 'Failed to load more sets', id: 'home-load-more', onRetry: () => loadMoreSets() });
    } finally {
      if (loadMoreControllerRef.current === controller) {
        loadMoreControllerRef.current = null;
        setLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    activeFilterKeyRef.current = JSON.stringify({ selectedTheme, searchQuery, sortBy, multibuildFilter, selectedLabels, catalogShop });
    loadMoreControllerRef.current?.abort();
    return () => loadMoreControllerRef.current?.abort();
  }, [selectedTheme, searchQuery, sortBy, multibuildFilter, selectedLabels, catalogShop]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // When the sentinel element is visible, load more
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          loadMoreSets();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '300px', // Start loading 300px before reaching the sentinel
      }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMore, loading, loadingMore, offset, selectedTheme, searchQuery, sortBy, multibuildFilter, selectedLabels, catalogShop]);

  useEffect(() => {
    // The search box now lives mid-hero, so "user scrolled a bit" is no longer
    // the same as "the box is stuck under the header". Measure its actual
    // position instead: sticky top is 70, so <= 71 means it is pinned.
    const handleScroll = () => {
      const el = stickyBoxRef.current;
      if (!el) return;
      setIsSticky(el.getBoundingClientRect().top <= 71);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  // Coordinate with the compact header search. We want exactly one search box
  // visible at a time, handing off when the big bar is halfway up the screen:
  // - Big bar's middle is still in the lower half of the viewport (just peeking
  //   in from the bottom, or below the fold): SHOW the header search.
  // - Big bar's middle has scrolled into the upper half / stuck under the
  //   header: HIDE the header search - the big bar is now the active one.
  // Using the element's position (not IntersectionObserver ratio) because ratio
  // reads 100% as soon as the whole bar fits on screen, even while it's still
  // sitting at the very bottom - which hid the header search far too early.
  useEffect(() => {
    if (!homeSearch) return;
    const update = () => {
      const wrapper = searchBarRef.current;
      if (!wrapper) return;
      // Measure the actual input element, not the wrapper (which carries margin
      // and sits inside padded sticky box - that skewed the geometry).
      const input = wrapper.querySelector('.main-search-input') || wrapper;
      const rect = input.getBoundingClientRect();
      // How much of the input is currently on screen (0–1).
      const visible = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
      const visibleRatio = rect.height > 0 ? visible / rect.height : 0;
      // Hand off to the big bar once at least half of the input is on screen, so
      // there's never a gap where neither search box is usable.
      homeSearch.setMainSearchVisible(visibleRatio >= 0.5);
    };
    update();
    const raf = requestAnimationFrame(update);
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    // The hero cards (stats, "Continue checking", recently added) load async and
    // shift the search bar's position after mount. A ResizeObserver on the page
    // re-measures whenever that happens, keeping the initial handoff state right.
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(document.body);
    }
    return () => {
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [homeSearch]);

  const toggleTheme = (theme) => {
    setSelectedTheme(selectedTheme === theme ? null : theme);
  };

  // Stats & highlights state (seeded server-side; community progress stays
  // client-side because it depends on the visitor's timezone)
  const [stats, setStats] = useState(initialData?.stats ?? null);
  const [highlights, setHighlights] = useState(initialData?.highlights ?? null);
  const [communityProgress, setCommunityProgress] = useState(null);

  // Continue checking (check-mode progress saved by the set page)
  const [continueSets, setContinueSets] = useState([]);
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);

  const loadContinueSets = useCallback(async () => {
    const byId = new Map();
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('bricksperbag_progress_')) {
          const data = JSON.parse(localStorage.getItem(key));
          if (data && data.total > 0 && data.checked > 0 && data.checked < data.total) {
            byId.set(String(data.id), data);
          }
        }
      }
    } catch (e) { /* localStorage unavailable */ }
    // Ingelogd: gesyncte voortgang van het account mergen, zodat "Continue
    // checking" ook cross-device werkt. Nieuwste voortgang wint per set.
    if (account) {
      try {
        const res = await fetchAllProgress();
        (res.progress || []).forEach(p => {
          if (!(p.total > 0 && p.checked > 0 && p.checked < p.total)) return;
          const entry = {
            id: p.set_id,
            name: p.name || p.set_id,
            image_url: p.image_url,
            checked: p.checked,
            total: p.total,
            updatedAt: p.updated_at ? new Date(p.updated_at).getTime() : 0,
          };
          const existing = byId.get(String(p.set_id));
          if (!existing || (entry.updatedAt || 0) > (existing.updatedAt || 0)) {
            byId.set(String(p.set_id), entry);
          }
        });
      } catch (e) { /* offline of verlopen token - lokale voortgang blijft */ }
    }
    const entries = [...byId.values()].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    setContinueSets(entries.slice(0, 3));
  }, [account]);

  useEffect(() => {
    // Full localStorage scan + JSON.parse per entry - defer it off the
    // hydration critical path. Re-runs when the account resolves, so the
    // server merge happens as soon as we know who's logged in.
    if (typeof requestIdleCallback === 'function') {
      const idleId = requestIdleCallback(loadContinueSets);
      return () => cancelIdleCallback(idleId);
    }
    const timer = setTimeout(loadContinueSets, 200);
    return () => clearTimeout(timer);
  }, [loadContinueSets]);

  const removeCheckProgress = useCallback(async (setId) => {
    try {
      localStorage.removeItem(`bricksperbag_progress_${setId}`);
      localStorage.removeItem(`bricksperbag_check_${setId}`);
      localStorage.removeItem(`bricksperbag_checkmode_${setId}`);
    } catch (e) { /* localStorage unavailable */ }
    // Ook de gesyncte voortgang wissen - anders komt de kaart bij de
    // volgende scan gewoon terug van de server
    if (account) {
      try { await deleteProgress(setId); } catch (e) { /* offline - kaart komt terug, bewust */ }
    }
    setConfirmRemoveId(null);
    // Re-scan so a 4th in-progress set can take the freed slot
    loadContinueSets();
  }, [account, loadContinueSets]);

  // Interactive sort mode demo state
  const [demoCounts, setDemoCounts] = useState({});
  const [demoAutoPlaying, setDemoAutoPlaying] = useState(false);
  const [demoUserInteracted, setDemoUserInteracted] = useState(false);
  const [demoVisible, setDemoVisible] = useState(false);
  const demoRef = useRef(null);
  // De demo rendert de échte SortPartTile - dezelfde tegel, gebaren en stijl
  // als in Sort mode, dus hij kan nooit uit de pas lopen. Parts in de vorm
  // die de tegel verwacht (partId = element-ID, imageUrl relatief).
  const demoParts = [
    { partId: '302324', quantity: 2, name: 'Plate 1 x 2', colorName: 'Yellow', colorHex: 'F2CD37', imageUrl: 'parts/302324.jpg' },
    { partId: '302021', quantity: 2, name: 'Plate 2 x 4', colorName: 'Red', colorHex: 'C91A09', imageUrl: 'parts/302021.jpg' },
    { partId: '302223', quantity: 1, name: 'Plate 2 x 2', colorName: 'Blue', colorHex: '0055BF', imageUrl: 'parts/302223.jpg' },
    { partId: '4107758', quantity: 2, name: 'Plate 1 x 3', colorName: 'Green', colorHex: '237841', imageUrl: 'parts/4107758.jpg' },
    { partId: '6059185', quantity: 3, name: 'Brick Special 1 x 2 Palisade [aka Log]', colorName: 'White', colorHex: 'FFFFFF', imageUrl: 'parts/6059185.jpg' },
    { partId: '4211414', quantity: 3, name: 'Tile 1 x 2 with Groove', colorName: 'Light Bluish Gray', colorHex: 'A0A5A9', imageUrl: 'parts/4211414.jpg' },
  ];
  const demoTotalPieces = demoParts.reduce((sum, p) => sum + p.quantity, 0);
  const demoCheckedPieces = demoParts.reduce((sum, p) => sum + Math.min(demoCounts[p.partId] || 0, p.quantity), 0);
  const demoProgress = (demoCheckedPieces / demoTotalPieces) * 100;

  // Auto-play demo animation - increment one piece at a time
  useEffect(() => {
    if (!demoAutoPlaying || demoUserInteracted) return;
    // Build sequence of increments: [partId, partId, ...] one per piece
    const order = ['302021', '4107758', '302324', '4211414', '6059185', '302223'];
    const sequence = [];
    order.forEach(id => {
      const part = demoParts.find(p => p.partId === id);
      for (let i = 0; i < part.quantity; i++) sequence.push(id);
    });
    const timeouts = [];
    sequence.forEach((id, i) => {
      timeouts.push(setTimeout(() => {
        setDemoCounts(prev => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
      }, 1500 + i * 700));
    });
    // After all checked, wait then reset and loop
    timeouts.push(setTimeout(() => {
      setDemoCounts({});
      setDemoAutoPlaying(false);
      setTimeout(() => setDemoAutoPlaying(true), 50);
    }, 1500 + sequence.length * 700 + 2500));
    return () => timeouts.forEach(clearTimeout);
  }, [demoAutoPlaying, demoUserInteracted]);

  // SortPartTile meldt acties als (partKey, quantity, action) - partKey is
  // `${bagId}_${partId}_nocolor` met bagId 'demo'
  const handleDemoToggle = useCallback((partKey, quantity, action) => {
    setDemoUserInteracted(true);
    setDemoAutoPlaying(false);
    const partId = partKey.split('_')[1];
    setDemoCounts(prev => {
      const cur = prev[partId] || 0;
      let next = cur;
      if (action === 'increment') next = Math.min(cur + 1, quantity);
      else if (action === 'decrement') next = Math.max(0, cur - 1);
      else if (action === 'complete') next = quantity;
      else if (action === 'reset') next = 0;
      return { ...prev, [partId]: next };
    });
  }, []);

  const toggleDemo = () => {
    if (!demoVisible) {
      setDemoVisible(true);
      setDemoCounts({});
      setDemoUserInteracted(false);
      setDemoAutoPlaying(true);
      setTimeout(() => {
        if (demoRef.current) {
          demoRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else {
      setDemoVisible(false);
      setDemoAutoPlaying(false);
    }
  };

  useEffect(() => {
    if (!initialData?.stats) api.fetchPublicStats().then(setStats).catch(() => {});
    if (!initialData?.highlights) api.fetchSetHighlights().then(setHighlights).catch(() => {});
    fetchCommunityProgress().then(setCommunityProgress).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The active matching set closest to done - shown as a live progress bar in
  // the Contribute card ("help finish it" beats a static description).
  const contributeSet = (() => {
    if (!communityProgress?.sets?.length) return null;
    let best = null;
    for (const s of communityProgress.sets) {
      const total = parseInt(s.total_steps, 10) || 0;
      const verified = parseInt(s.verified_steps, 10) || 0;
      if (total === 0) continue;
      const pct = Math.round((verified / total) * 100);
      if (pct >= 100) continue;
      if (!best || pct > best.pct) best = { name: s.set_name, pct };
    }
    return best;
  })();

  // Alle filtering gebeurt nu op backend
  const filteredSets = legoSets;

  const mainSearchSuggestions = searchInput.trim().length >= 2 &&
    searchInput.trim().toLowerCase() === searchQuery.trim().toLowerCase()
    ? legoSets.slice(0, 6)
    : [];
  const mainSearchSuggestionsOpen = mainSearchFocused && !mainSearchDismissed &&
    !loading && !error && mainSearchSuggestions.length > 0;
  const mainSearchActiveOptionId = mainSearchSuggestionsOpen && mainSearchActive >= 0
    ? `${homeSearchListboxId}-option-${mainSearchActive}`
    : undefined;

  const openMainSearchSuggestion = (set) => {
    trackSearchResultOpen();
    setMainSearchFocused(false);
    setMainSearchActive(-1);
    router.push(`/set/${set.id}`);
  };

  const handleMainSearchKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (mainSearchSuggestions.length === 0) return;
      event.preventDefault();
      setMainSearchDismissed(false);
      setMainSearchActive((current) => {
        if (current < 0) return event.key === 'ArrowDown' ? 0 : mainSearchSuggestions.length - 1;
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        return (current + direction + mainSearchSuggestions.length) % mainSearchSuggestions.length;
      });
    } else if (event.key === 'Enter' && mainSearchSuggestionsOpen && mainSearchActive >= 0) {
      event.preventDefault();
      openMainSearchSuggestion(mainSearchSuggestions[mainSearchActive]);
    } else if (event.key === 'Escape') {
      if (mainSearchSuggestionsOpen) event.preventDefault();
      setMainSearchDismissed(true);
      setMainSearchActive(-1);
    } else if (event.key === 'Tab') {
      setMainSearchFocused(false);
      setMainSearchActive(-1);
    }
  };

  const showHero = !searchQuery && !selectedTheme && !multibuildFilter && !selectedLabels;

  // Non-stuck background blends with the hero when it's visible
  const currentBgColor = isSticky ? '#f0f0f0' : (showHero ? '#f8f9fa' : '#f7f7f6');

  return (
    <>
      {!showHero && <h1 className="sr-only">Browse LEGO sets</h1>}
      {/* Hero Section - only when no search/filter active */}
      {showHero && (
        <Box className="stud-overlay" style={{ background: 'linear-gradient(180deg, #EAF2F8 0%, #f8f9fa 100%)' }}>
          <Container size="xl" pt="xl" pb="xs">
            {/* Tagline - the live numbers ARE the stats: a claim in the sentence
                beats a separate stats strip */}
            <Box ta="center" pt="md">
              <Title order={1} style={{ color: '#212529', fontSize: 'clamp(28px, 5vw, 40px)', lineHeight: 1.2, fontWeight: 800, letterSpacing: '-0.5px' }}>
                Every brick in <span className="hero-title-accent">the right bag</span>
              </Title>
              <Text c="dimmed" size="lg" maw={640} mx="auto" mt="sm" className="hero-sub-full">
                {stats?.totalSets ? (
                  <>
                    Know exactly which of <Text component="span" fw={700} style={{ color: '#212529' }}>{stats.totalPieces?.toLocaleString('en-US')} parts</Text> goes in which bag.
                    Organize <Text component="span" fw={700} style={{ color: '#212529' }}>{stats.totalSets.toLocaleString('en-US')} LEGO sets</Text> by their <Text component="span" fw={700} style={{ color: '#212529' }}>{stats.totalBags?.toLocaleString('en-US')} numbered bags</Text> to verify
                    completeness, rebuild accurately, or prepare for resale.
                  </>
                ) : (
                  'Know exactly which parts are in each numbered bag. Organize your LEGO sets, verify completeness, rebuild accurately, or prepare for resale.'
                )}
              </Text>
              {/* Mobiel: één regel i.p.v. vijf, zodat de sets eerder in beeld komen */}
              <Text c="dimmed" size="md" mx="auto" mt={6} className="hero-sub-short">
                {stats?.totalSets ? (
                  <>
                    See which parts are in every bag of{' '}
                    <Text component="span" fw={700} style={{ color: '#212529' }}>{stats.totalSets.toLocaleString('en-US')} LEGO sets</Text>.
                  </>
                ) : (
                  'See which parts are in every numbered LEGO bag.'
                )}
              </Text>
            </Box>
          </Container>
        </Box>
      )}

      {/* Sticky Search Bar - the primary action, directly under the tagline;
          sticks under the header while scrolling the grid */}
      <Box
        ref={stickyBoxRef}
        style={{
          position: 'sticky',
          top: 70,
          left: 0,
          right: 0,
          width: '100%',
          zIndex: 100,
          backgroundColor: currentBgColor,
          boxShadow: isSticky ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
          transition: 'all 0.3s ease',
          padding: isSticky ? '16px 0' : '20px 0',
        }}
      >
        <Container size="xl">
          {/* Wrapper watched by the header-search observer - measures only the
              input field's visibility, not the whole sticky block. */}
          <div ref={searchBarRef} style={{ position: 'relative' }}>
            <label htmlFor="home-search" className="sr-only">Search LEGO sets</label>
            <TextInput
              ref={mainSearchInputRef}
              id="home-search"
              placeholder="Search sets by name or number…"
              size="lg"
              value={searchInput}
              onChange={(event) => {
                setSearchQuery(event.currentTarget.value);
                setMainSearchDismissed(false);
                setMainSearchActive(-1);
              }}
              onFocus={() => {
                setMainSearchFocused(true);
                setMainSearchDismissed(false);
              }}
              onBlur={() => window.setTimeout(() => setMainSearchFocused(false), 0)}
              onKeyDown={handleMainSearchKeyDown}
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={mainSearchSuggestionsOpen}
              aria-controls={mainSearchSuggestionsOpen ? homeSearchListboxId : undefined}
              aria-activedescendant={mainSearchActiveOptionId}
              leftSection={<IconSearch size={20} aria-hidden="true" style={{ color: '#66707a' }} />}
              rightSection={
                searchInput ? (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    radius="xl"
                    aria-label="Clear set search"
                    onClick={() => {
                      setSearchQuery('');
                      setMainSearchActive(-1);
                      mainSearchInputRef.current?.focus();
                    }}
                  >
                    <IconX size={18} aria-hidden="true" />
                  </ActionIcon>
                ) : null
              }
              rightSectionPointerEvents="all"
              radius="xl"
              classNames={{ input: 'main-search-input' }}
            />
            <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
              {loading && searchInput.trim().length >= 2
                ? 'Searching LEGO sets.'
                : searchInput.trim().length >= 2 && !error
                  ? `${totalCount ?? legoSets.length} matching ${(totalCount ?? legoSets.length) === 1 ? 'set' : 'sets'} found.`
                  : ''}
            </span>
            {mainSearchSuggestionsOpen && (
              <Paper
                id={homeSearchListboxId}
                role="listbox"
                aria-label="Matching LEGO sets"
                shadow="lg"
                radius="lg"
                className="home-search-suggestions"
              >
                {mainSearchSuggestions.map((suggestion, index) => (
                  <Link
                    key={suggestion.id}
                    id={`${homeSearchListboxId}-option-${index}`}
                    href={`/set/${suggestion.id}`}
                    role="option"
                    aria-selected={index === mainSearchActive}
                    tabIndex={-1}
                    className="search-suggestion"
                    data-active={index === mainSearchActive}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setMainSearchActive(index)}
                    onClick={() => {
                      trackSearchResultOpen();
                      setMainSearchFocused(false);
                    }}
                  >
                    <Box className="home-search-suggestion-image">
                      <Image
                        src={suggestion.image_url ? getThumbUrl(suggestion.image_url, 96) : 'https://placehold.co/36x36?text=?'}
                        alt=""
                        fit="contain"
                        w={40}
                        h={40}
                        style={{ mixBlendMode: 'multiply' }}
                      />
                    </Box>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" fw={700} lineClamp={1} style={{ color: '#212529' }}>{suggestion.name}</Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>#{suggestion.id}{suggestion.theme ? ` · ${suggestion.theme}` : ''}</Text>
                    </Box>
                    <IconArrowRight size={16} aria-hidden="true" color="#66707a" />
                  </Link>
                ))}
              </Paper>
            )}
          </div>
        </Container>
      </Box>

      {/* Hero continuation: feature doors, demo, personal rows */}
      {showHero && (
        <Box style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
          <Container size="xl" pt="md" pb="xl">
            {/* Feature cards */}
            <SimpleGrid cols={{ base: 2, sm: MULTI_BUILD_PROMO_ENABLED ? 4 : 3 }} spacing={{ base: 'xs', sm: 'md' }} mb="xl" className="feature-grid">
              <Paper
                component="button"
                type="button"
                p="lg" radius="lg" ta="center"
                style={{ backgroundColor: demoVisible ? '#fff9db' : '#fff', border: demoVisible ? '1px solid #ffe066' : '1px solid #e9ecef', cursor: 'pointer', position: 'relative' }}
                className="feature-card"
                aria-expanded={demoVisible}
                aria-controls="sort-mode-demo"
                onClick={() => { trackEvent('Home Tile Clicked', { tile: 'sort-demo' }); toggleDemo(); }}
              >
                {isSortModeNew() && (
                  <Badge size="sm" variant="filled" color="yellow" radius="xl" className="feature-card-badge" style={{ position: 'absolute', top: 10, right: 10, color: '#212529' }}>
                    New
                  </Badge>
                )}
                <Box className="feature-icon-bubble" style={{ backgroundColor: '#fff4e6' }}>
                  <IconChecklist size={28} color="#e8590c" stroke={1.7} />
                </Box>
                <Text fw={700} size="sm" mb={4} className="feature-card-title" style={{ color: '#212529' }}>Sort Mode</Text>
                <AmbientBrickProgress />
                <Text size="xs" c="dimmed" className="feature-card-desc">Tick off every part to sort your set bag by bag and spot what&apos;s missing. Click to try the live demo.</Text>
              </Paper>
              {/* Multi-build: unieke feature (Browse Sets-kaart was dubbelop -
                  de catalogus staat direct onder de vouw). Tijdelijk uit tot de
                  backfill compleet is - zie MULTI_BUILD_PROMO_ENABLED */}
              {MULTI_BUILD_PROMO_ENABLED && (
              <Paper
                component="button"
                type="button"
                p="lg" radius="lg" ta="center"
                style={{ flex: 1, cursor: 'pointer', position: 'relative' }}
                className="feature-card"
                onClick={() => {
                  trackEvent('Home Tile Clicked', { tile: 'multibuild' });
                  // Nieuwste eerst: multi-build is een groeiende collectie,
                  // dus recent toegevoegde sets zijn hier het interessantst
                  updateParams({ multibuild: '1', sort: 'newest' });
                }}
              >
                {isMultiBuildNew() && (
                  <Badge size="sm" variant="filled" color="red" radius="xl" className="feature-card-badge" style={{ position: 'absolute', top: 10, right: 10, backgroundColor: '#c92a2a' }}>
                    New
                  </Badge>
                )}
                <Box className="feature-icon-bubble" style={{ backgroundColor: '#fff5f5' }}>
                  <IconHammer size={28} color="#e03131" stroke={1.7} />
                </Box>
                <Text fw={700} size="sm" mb={4} className="feature-card-title" style={{ color: '#212529' }}>Multi-Build Sets</Text>
                <Text size="xs" c="dimmed" className="feature-card-desc">Building the B-model of a 3-in-1 set? Pick a build and sort only the parts it needs</Text>
              </Paper>
              )}
              <Link href="/findmybag" style={{ textDecoration: 'none', display: 'flex' }} onClick={() => trackEvent('Home Tile Clicked', { tile: 'findmybag' })}>
                <Paper
                  p="lg" radius="lg" ta="center" style={{ flex: 1 }}
                  className="feature-card"
                >
                  <Box className="feature-icon-bubble" style={{ backgroundColor: '#e6fcf5' }}>
                    <IconZoomQuestion size={28} color="#099268" stroke={1.7} />
                  </Box>
                  <Text fw={700} size="sm" mb={4} className="feature-card-title" style={{ color: '#212529' }}>Find My Bag</Text>
                  <Text size="xs" c="dimmed" className="feature-card-desc">Enter brick IDs to find which bag they belong to</Text>
                </Paper>
              </Link>
              {/* Straight to the action - /matchparts explains itself to new users
                  ("How it works" + Learn more → /contribute) */}
              <Link href="/matchparts" style={{ textDecoration: 'none', display: 'flex' }} onClick={() => trackEvent('Home Tile Clicked', { tile: 'contribute' })}>
                <Paper
                  p="lg" radius="lg" ta="center" style={{ flex: 1 }}
                  className="feature-card"
                >
                  <Box className="feature-icon-bubble" style={{ backgroundColor: '#fff0f0' }}>
                    <IconUsers size={28} color="#e03131" stroke={1.7} />
                  </Box>
                  <Text fw={700} size="sm" mb={4} className="feature-card-title" style={{ color: '#212529' }}>Contribute</Text>
                  {contributeSet ? (
                    <>
                      <BrickProgress
                        value={contributeSet.pct} size="sm"
                        aria-label={`${contributeSet.name} matching progress`}
                        style={{ maxWidth: 150, margin: '2px auto 6px' }}
                      />
                      <Text size="xs" c="dimmed" lineClamp={2} className="feature-card-desc">
                        {contributeSet.name} is {contributeSet.pct}% verified. Help finish it.
                      </Text>
                    </>
                  ) : (
                    <Text size="xs" c="dimmed" className="feature-card-desc">Help verify part matches and grow our database</Text>
                  )}
                </Paper>
              </Link>
            </SimpleGrid>

            {/* Check Mode Demo - shown on click */}
            {demoVisible && (
              <Paper id="sort-mode-demo" ref={demoRef} radius="lg" p="lg" mb="xl" style={{ backgroundColor: '#fff', border: '1px solid #a5d8ff', boxShadow: '0 4px 16px rgba(34,139,230,0.08)', animation: 'demoSlideIn 0.3s ease' }}>
                <Group justify="space-between" mb="sm">
                  <Group gap="xs">
                    <IconChecklist size={18} color="#1971c2" />
                    <Text fw={700} size="sm" c="#1971c2">Sort Mode</Text>
                  </Group>
                  <Group gap="xs">
                    {demoCheckedPieces > 0 && (
                      <ActionIcon
                        size="sm" color="gray" variant="subtle"
                        title="Reset demo" aria-label="Reset demo"
                        onClick={() => { setDemoCounts({}); setDemoUserInteracted(true); setDemoAutoPlaying(false); }}
                      >
                        <IconRefresh size={14} />
                      </ActionIcon>
                    )}
                    <Badge size="sm" color={demoProgress === 100 ? 'green' : 'blue'} variant="light" radius="xl">
                      {demoProgress === 100 ? 'Complete!' : 'Bag 3'}
                    </Badge>
                  </Group>
                </Group>
                <Text size="xs" c="dimmed" mb="sm">Tap to count pieces one by one. Long-press to check off a part at once.</Text>
                <BrickProgress value={demoProgress} size="sm" aria-label="Demo progress" style={{ marginBottom: 'var(--mantine-spacing-md)' }} />
                {(() => {
                  const remainingParts = demoParts.filter(p => (demoCounts[p.partId] || 0) < p.quantity);
                  const foundParts = demoParts.filter(p => (demoCounts[p.partId] || 0) >= p.quantity);
                  // Zelfde ondergrond + grid als de echte Sort mode-stage,
                  // met de echte SortPartTile erin
                  const demoGridStyle = {
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))',
                    gap: 10,
                  };
                  return (
                    <Box p="sm" style={{ backgroundColor: '#f5f6f8', borderRadius: 12 }}>
                      {remainingParts.length > 0 ? (
                        <Box style={demoGridStyle}>
                          {remainingParts.map((part) => (
                            <SortPartTile
                              key={part.partId}
                              part={part}
                              bagId="demo"
                              checkedCount={demoCounts[part.partId] || 0}
                              onToggleCheck={handleDemoToggle}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Box p="md" style={{ textAlign: 'center', backgroundColor: '#d3f9d8', borderRadius: 10 }}>
                          <IconCheck size={22} color="#2f9e44" />
                          <Text size="sm" fw={600} c="#2f9e44">Bag 3 complete! Ready for the next bag.</Text>
                        </Box>
                      )}
                      {foundParts.length > 0 && (
                        <Box mt="sm">
                          <Group gap={6} mb={6} pl={4}>
                            <IconCheck size={14} color="#2f9e44" />
                            <Text size="xs" fw={600} c="#2f9e44">Found ({foundParts.length})</Text>
                          </Group>
                          <Box style={demoGridStyle}>
                            {foundParts.map((part) => (
                              <SortPartTile
                                key={`found-${part.partId}`}
                                part={part}
                                bagId="demo"
                                checkedCount={demoCounts[part.partId] || 0}
                                inFoundSection
                                onToggleCheck={handleDemoToggle}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })()}
                {demoProgress < 100 && (
                  <Text size="xs" c="dimmed" mt="sm" ta="center" style={{ fontStyle: 'italic' }}>
                    {demoUserInteracted
                      ? `${demoCheckedPieces}/${demoTotalPieces} pieces`
                      : 'Try it! Click or hold the bricks'}
                  </Text>
                )}
                <Link href="/set/77092/sort" style={{ textDecoration: 'none', display: 'block', marginTop: 12 }}>
                  <Group justify="center" gap={6}>
                    <Text size="xs" fw={600} c="#228be6">Try Sort Mode on a real set</Text>
                    <IconArrowRight size={14} color="#228be6" />
                  </Group>
                </Link>
              </Paper>
            )}

            {/* Continue checking - resume check-mode progress */}
            {continueSets.length > 0 && (
              <Box mb="xl">
                <Group gap="xs" mb="sm">
                  <IconChecklist size={18} color="#1971c2" />
                  <Text fw={700} size="sm" style={{ color: '#495057' }}>Continue sorting</Text>
                </Group>
                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                  {continueSets.map((entry) => {
                    const pct = Math.round((entry.checked / entry.total) * 100);
                    return (
                      <Paper
                        key={entry.id}
                        component="article"
                        radius="lg" p="md" className="feature-card"
                        style={{ display: 'flex', alignItems: 'center', gap: 14, position: 'relative' }}
                      >
                          <Link
                            href={`/set/${entry.id}/sort`}
                            className="continue-set-link-overlay"
                            aria-label={`Continue sorting ${entry.name}`}
                          />
                          <Popover
                            width={210} position="bottom-end" withArrow shadow="md"
                            opened={confirmRemoveId === entry.id}
                            onChange={(opened) => { if (!opened) setConfirmRemoveId(null); }}
                          >
                            <Popover.Target>
                              <ActionIcon
                                variant="subtle" color="gray" size="sm" radius="xl"
                                aria-label={`Remove sorting progress for ${entry.name}`}
                                style={{ position: 'absolute', top: 6, right: 6, zIndex: 3 }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setConfirmRemoveId(confirmRemoveId === entry.id ? null : entry.id);
                                }}
                              >
                                <IconX size={14} />
                              </ActionIcon>
                            </Popover.Target>
                            <Popover.Dropdown onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                              <Text size="sm" fw={600} mb={4}>Remove progress?</Text>
                              <Text size="xs" c="dimmed" mb="xs">
                                This resets all {entry.checked} checked pieces of this set.
                              </Text>
                              <Group gap="xs" justify="flex-end">
                                <Button size="compact-xs" variant="default" onClick={() => setConfirmRemoveId(null)}>
                                  Cancel
                                </Button>
                                <Button size="compact-xs" color="red" onClick={() => removeCheckProgress(entry.id)}>
                                  Reset
                                </Button>
                              </Group>
                            </Popover.Dropdown>
                          </Popover>
                          <Box style={{
                            width: 64, height: 64, borderRadius: 10, backgroundColor: '#f8f9fa',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden',
                          }}>
                            <Image
                              src={entry.image_url ? getThumbUrl(entry.image_url, 192) : 'https://placehold.co/56x56?text=?'}
                              alt={entry.name}
                              fit="contain" w={56} h={56}
                              style={{ mixBlendMode: 'multiply' }}
                            />
                          </Box>
                          <Box style={{ flex: 1, minWidth: 0 }}>
                            <Text size="sm" fw={700} lineClamp={1} style={{ color: '#212529' }}>{entry.name}</Text>
                            <BrickProgress value={pct} size="sm" aria-label={`${entry.name} progress`} style={{ margin: '6px 0' }} />
                            <Group gap={6} justify="space-between">
                              <Text size="xs" c="dimmed" fw={600}>{entry.checked}/{entry.total} pieces</Text>
                              <Text size="xs" fw={700} style={{ color: '#1971c2' }}>{pct}%</Text>
                            </Group>
                          </Box>
                          <IconArrowRight size={18} color="#228be6" style={{ flexShrink: 0 }} />
                      </Paper>
                    );
                  })}
                </SimpleGrid>
              </Box>
            )}

            {/* Recently Added */}
            {highlights?.recentSets?.length > 0 && (
              <Box>
                <Group justify="space-between" mb="sm">
                  <Group gap="xs">
                    <IconSparkles size={18} color="#868e96" />
                    <Text fw={700} size="sm" style={{ color: '#495057' }}>Recently added</Text>
                  </Group>
                  <Button
                    type="button"
                    variant="subtle"
                    color="gray"
                    size="compact-xs"
                    style={{ color: '#495057' }}
                    rightSection={<IconArrowRight size={12} aria-hidden="true" />}
                    onClick={() => {
                      setSortBy('newest');
                      if (searchBarRef.current) {
                        const top = searchBarRef.current.getBoundingClientRect().top + window.scrollY - 70;
                        window.scrollTo({ top, behavior: 'smooth' });
                      }
                    }}
                  >
                    View all
                  </Button>
                </Group>
                <Box style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
                  {highlights.recentSets.slice(0, 14).map((set) => (
                    <Link key={set.id} href={`/set/${set.id}`} style={{ textDecoration: 'none', flexShrink: 0 }}>
                      <Paper
                        radius="lg" p="xs"
                        style={{
                          width: 140, backgroundColor: '#fff', border: '1px solid #e9ecef',
                          cursor: 'pointer', transition: 'all 0.2s ease',
                        }}
                        className="recent-set-card"
                      >
                        <Box style={{ width: '100%', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6, position: 'relative' }}>
                          <Image
                            src={set.image_url ? getThumbUrl(set.image_url, 192) : undefined}
                            alt={set.name}
                            fit="contain"
                            h={100} w={100}
                            style={{ mixBlendMode: 'multiply' }}
                          />
                          <VerificationBadge
                            verified={set.verified}
                            source={set.source}
                            showUnverified={true}
                            iconOnly={true}
                            size="xs"
                            style={{ position: 'absolute', top: 2, right: 2 }}
                          />
                        </Box>
                        <Text size="xs" fw={600} lineClamp={1} style={{ color: '#212529' }}>{set.name}</Text>
                        <Text size="xs" c="dimmed">#{set.id}</Text>
                      </Paper>
                    </Link>
                  ))}
                </Box>
              </Box>
            )}
          </Container>
        </Box>
      )}

      {/* Results Grid */}
      <Container
        size="xl"
        pt="xs"
        pb="xl"
        ref={gridRef}
        aria-labelledby="set-results-heading"
        aria-busy={loading || loadingMore}
      >
          <h2 id="set-results-heading" className="sr-only">LEGO set search results</h2>
          {/* Toolbar: result count, theme filter, sort - lives with the grid it controls */}
          <Group justify="space-between" align="center" mb="sm">
            <Text size="sm" c="dimmed" role="status" aria-live="polite" aria-atomic="true" style={{ minWidth: 80 }}>
              {!loading && totalCount !== null ? `${totalCount} result${totalCount === 1 ? '' : 's'}` : '\u00A0'}
            </Text>
            <Group gap={8}>
              {multibuildFilter && (
                <Badge
                  component="button"
                  type="button"
                  variant="light" size="lg" radius="xl"
                  aria-label="Remove multi-build sets filter"
                  rightSection={<IconX size={12} aria-hidden="true" style={{ cursor: 'pointer' }} />}
                  onClick={() => updateParams({ multibuild: null })}
                  style={{
                    backgroundColor: '#fff5f5', color: '#e03131', border: '1px solid #ffc9c9',
                    textTransform: 'none', fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Multi-build sets
                </Badge>
              )}
              <NativeSelect
                size="xs"
                aria-label="Filter sets by theme"
                value={selectedTheme || ''}
                onChange={(e) => setSelectedTheme(e.currentTarget.value || null)}
                data={[
                  { value: '', label: 'All themes' },
                  ...themes.map(t => ({ value: t, label: t }))
                ]}
                styles={{
                  input: {
                    border: '1.5px solid #dee2e6',
                    borderRadius: '20px',
                    backgroundColor: '#fff',
                    paddingLeft: '12px',
                    paddingRight: '28px',
                    fontWeight: 500,
                    color: '#495057',
                    cursor: 'pointer',
                    maxWidth: '120px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
              <CatalogFilters catalog={catalog} labels={selectedLabels} onChange={updateParams} />
              <NativeSelect
                size="xs"
                aria-label="Sort LEGO sets"
                value={sortBy}
                onChange={(e) => setSortBy(e.currentTarget.value)}
                data={[
                  { value: 'popular', label: 'Popular' },
                  { value: 'newest', label: 'Recently added' },
                  { value: 'pieces', label: 'Most pieces' },
                  { value: 'name', label: 'Name A-Z' },
                ]}
                styles={{
                  input: {
                    border: '1.5px solid #dee2e6',
                    borderRadius: '20px',
                    backgroundColor: '#fff',
                    paddingLeft: '12px',
                    paddingRight: '28px',
                    fontWeight: 500,
                    color: '#495057',
                    cursor: 'pointer',
                  },
                }}
              />
            </Group>
          </Group>

        {loading && (
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} role="status" aria-label="Loading LEGO sets">
            {Array.from({ length: 8 }).map((_, i) => (
              <Card key={i} padding={0} radius="lg" style={{ overflow: 'hidden', border: '1px solid #e9ecef', backgroundColor: '#fff' }}>
                <Skeleton height={0} pb="100%" radius={0} animate />
                <Box p="md">
                  <Skeleton height={10} width="40%" radius="xl" mb={10} animate />
                  <Skeleton height={14} width="85%" radius="xl" animate />
                </Box>
              </Card>
            ))}
          </SimpleGrid>
        )}

        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            mb="md"
            styles={{ title: { color: '#a61e1e' } }}
          >
            {error}
          </Alert>
        )}

        {!loading && !error && filteredSets.length === 0 && (
          <Center py={60}>
            <Stack align="center" gap="sm" maw={420}>
              <IconZoomQuestion size={48} stroke={1.3} color="#adb5bd" />
              <Text fw={700} size="lg" style={{ color: '#212529' }}>No sets found{searchQuery ? ` for “${searchQuery}”` : ''}</Text>
              <Text c="dimmed" size="sm" ta="center">
                Try a different name or set number{selectedTheme ? ', or clear the theme filter' : ''}. Missing a set you own?
              </Text>
              <Group gap="sm" mt={4}>
                {(searchQuery || selectedTheme) && (
                  <Button variant="default" radius="xl" size="sm" onClick={() => updateParams({ search: null, theme: null, multibuild: null })}>
                    Clear search
                  </Button>
                )}
                <Button component={Link} href="/request" radius="xl" size="sm" leftSection={<IconPackage size={16} />}>
                  Request this set
                </Button>
              </Group>
            </Stack>
          </Center>
        )}

        {!loading && !error && filteredSets.length > 0 && (
          <>
            <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }}>
              {filteredSets.map((set, index) => (
                <SetCard
                  key={set.id}
                  set={set}
                  priority={index < 8}
                  onYearClick={handleYearClick}
                  onOpen={searchQuery ? trackSearchResultOpen : undefined}
                />
              ))}
            </SimpleGrid>

            {/* Infinite scroll sentinel element */}
            <div ref={loadMoreRef} aria-hidden="true" style={{ height: '20px', margin: '20px 0' }} />

            {/* Loading indicator for infinite scroll */}
            {loadingMore && (
              <Center py="md" role="status" aria-label="Loading more LEGO sets">
                <Loader size="md" />
              </Center>
            )}
          </>
        )}
      </Container>
    </>
  );
}

export default function HomeClient({ initialData, initialQuery }) {
  return (
    <Suspense>
      <HomePageContent initialData={initialData} initialQuery={initialQuery} />
    </Suspense>
  );
}
